import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import AnimatedNumber from "@/components/AnimatedNumber";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, TrendingUp, Users, Target, BarChart3 } from "lucide-react";

const CASES = [
  {
    sector: "Education Sector",
    color: "saavi-blue",
    img: "https://images.pexels.com/photos/31656148/pexels-photo-31656148.jpeg",
    metrics: [
      { v: "300,000+", l: "qualified leads generated" },
      { v: "130%", l: "lead target growth achieved" },
      { v: "100%", l: "enrollment goals delivered" },
    ],
  },
  {
    sector: "Business Growth Campaigns",
    color: "saavi-red",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    metrics: [
      { v: "−35%", l: "reduction in Cost Per Lead" },
      { v: "+42%", l: "increase in conversion rates" },
      { v: "Significant", l: "ROAS improvement" },
    ],
  },
  {
    sector: "Social Media Growth",
    color: "saavi-green",
    img: "https://images.pexels.com/photos/6170176/pexels-photo-6170176.jpeg",
    metrics: [
      { v: "0 → 100K+", l: "followers built from scratch" },
      { v: "+300%", l: "engagement rate increase" },
      { v: "Multi-channel", l: "community development" },
    ],
  },
];

const OUTCOMES = [
  { icon: Target, label: "Lower Cost Per Lead", color: "text-saavi-blue" },
  { icon: Users, label: "Better Lead Quality", color: "text-saavi-red" },
  { icon: TrendingUp, label: "Higher Conversion Rates", color: "text-saavi-green" },
  { icon: BarChart3, label: "Improved ROI", color: "text-saavi-blue" },
];

export default function CaseStudies() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="Case Studies · Results"
        title={<>Growth <span className="text-saavi-red">stories</span> that speak for themselves.</>}
        description="Real numbers from real campaigns — across education, business, and social media growth."
        accent="red"
      />

      <section className="border-b border-border">
        <div className="container-saavi divide-y divide-border">
          {CASES.map((c, idx) => (
            <div key={c.sector} className="grid grid-cols-1 lg:grid-cols-12 gap-10 py-16 md:py-20 items-center">
              <Reveal variant={idx % 2 === 1 ? "slide-right" : "slide-left"} className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="overflow-hidden border border-border rounded-md group">
                  <img src={c.img} alt={c.sector} className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </Reveal>
              <Reveal variant={idx % 2 === 1 ? "slide-left" : "slide-right"} delay={120} className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className={`font-mono-overline text-${c.color} mb-4`}>Case 0{idx + 1}</div>
                <h2 className="text-3xl md:text-4xl mb-8">{c.sector}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {c.metrics.map((m, mi) => (
                    <Reveal key={m.l} variant="fade-up" delay={mi * 100}>
                      <div className="border border-border rounded-md p-5 bg-card lift-on-hover">
                        <div className={`text-3xl font-extrabold text-${c.color}`}>
                          <AnimatedNumber value={m.v} duration={1500 + mi * 200} />
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground leading-snug">{m.l}</div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad">
        <div className="container-saavi">
          <div className="font-mono-overline text-saavi-green mb-4">Outcomes We Deliver</div>
          <h2 className="text-4xl md:text-5xl max-w-3xl mb-12">Outcomes we consistently deliver across accounts.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {OUTCOMES.map(({ icon: Icon, label, color }) => (
              <div key={label} className="card-hover border border-border rounded-md p-6 bg-card">
                <Icon className={`h-6 w-6 ${color}`} />
                <p className="mt-5 text-lg font-semibold">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 flex flex-wrap gap-3">
            <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2">Replicate These Results <ArrowUpRight className="h-4 w-4" /></Button></Link>
            <Link to="/packages"><Button size="lg" variant="outline" className="h-12 px-6">View Packages</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
